import { inject, Injectable } from '@angular/core';
import {
  QuestionnaireResponse,
  UserPlanRequest,
  AppointmentInfoResponse,
  QuestionnaireStatus,
  BOOK_STATUS,
  SaveQuestionnaireRequest,
} from '../interfaces/reserve-health.model';
import { API_ENDPOINTS } from '../constants/api.constants';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { ApiResponse } from '../interfaces/api-response.model';
import { SharedStorageService } from './shared-storage.service';

/**
 * 健康調查表格相關服務
 */
@Injectable({
  providedIn: 'root',
})
export class ReserveHealthService {
  http: HttpClient = inject(HttpClient);
  private sharedStorageService = inject(SharedStorageService);

  // 問卷狀態管理 - 從 SharedStorage 初始化
  private questionnaireStatusSubject = new BehaviorSubject<QuestionnaireStatus>(
    this.getInitialQuestionnaireStatus()
  );
  public questionnaireStatus$ = this.questionnaireStatusSubject.asObservable();

  /**
   * 從 SharedStorage 的 myReservation 計算初始問卷狀態
   */
  private getInitialQuestionnaireStatus(): QuestionnaireStatus {
    try {
      console.log('ReserveHealthService: 計算初始問卷狀態');

      const myReservation = this.sharedStorageService.get('myReservation');
      const savedData = this.sharedStorageService.get('savedQuestionnaireData');

      // 如果沒有預約資料，可能是還在載入中，先返回 WAITING 避免閃爍
      if (!myReservation?.bookDate || !myReservation?.bookStatus) {
        console.log('ReserveHealthService: 沒有預約資料，返回 WAITING');
        return 'WAITING';
      }

      // 優先檢查是否已經有完成的問卷資料，避免閃爍
      if (savedData?.savedAt && savedData?.caseNo === myReservation?.caseNo) {
        console.log('ReserveHealthService: 發現已完成的問卷資料，返回 COMPLETED');
        return 'COMPLETED';
      }

      const baseStatus = this.calculateQuestionnaireStatusFromReservation(
        myReservation.bookDate,
        myReservation.bookStatus
      );

      console.log('ReserveHealthService: 計算基本狀態為', baseStatus);

      // 如果基本狀態是 AVAILABLE，進一步檢查是否已填寫
      if (baseStatus === 'AVAILABLE') {
        const hasData = this.checkIfQuestionnaireDataExistsInStorage();
        const finalStatus = hasData ? 'COMPLETED' : 'AVAILABLE';
        console.log('ReserveHealthService: AVAILABLE 狀態檢查，hasData:', hasData, '最終狀態:', finalStatus);
        return finalStatus;
      }

      return baseStatus;
    } catch (error) {
      console.error('ReserveHealthService: 計算初始問卷狀態失敗:', error);
      return 'WAITING';
    }
  }

  /**
   * 根據預約資訊計算問卷狀態（與 health-questionnaire.component 的邏輯一致）
   */
  private calculateQuestionnaireStatusFromReservation(
    bookDate: string,
    bookStatus: string
  ): QuestionnaireStatus {
    // 1. 計算距離健檢的天數
    const today = new Date();
    const checkDate = this.parseBookDate(bookDate);
    const daysUntil = Math.ceil((checkDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const isBookDatePassed = today > checkDate;

    // 2. 已預約但未報到且健檢日已過 → 逾期狀態
    if (bookStatus === '1' && isBookDatePassed) {
      return 'OVERDUE';
    }

    // 3. 預約狀態不是"1"(正常) → 無需填寫
    // bookStatus: "1"=正常, "3"=已報到, "8"=已列印報告, "9"=已寄發報告
    if (bookStatus !== '1') {
      return 'NOT_NEEDED';
    }

    // 4. 距離健檢 > 7天 → 等待開放
    if (daysUntil > 7) {
      return 'WAITING';
    }

    // 5. 距離健檢 ≤ 7天 → 可以填寫
    return 'AVAILABLE';
  }

  /**
   * 從 API 初始化問卷狀態（由 member component 調用）
   */
  initializeQuestionnaireStatusFromAPI(): void {
    console.log('ReserveHealthService: 從 API 初始化問卷狀態');

    // 先檢查 SharedStorage 中是否有預約資料
    const myReservation = this.sharedStorageService.get('myReservation');

    if (!myReservation?.caseNo) {
      console.log('ReserveHealthService: 沒有預約資料，無法初始化');
      return;
    }

    // 調用 CB-043 API 檢查問卷狀態
    // this.checkQuestionnaireStatusFromAPI(myReservation.caseNo);
  }

  /**
   * 調用 CB-043 API 檢查問卷狀態
   */
  // private checkQuestionnaireStatusFromAPI(caseNo: string): void {
  //   console.log('ReserveHealthService: 調用 CB-043 API 檢查問卷狀態，caseNo:', caseNo);

  //   // 先檢查健檢日期限制
  //   const myReservation = this.sharedStorageService.get('myReservation');
  //   if (!myReservation?.bookDate) {
  //     console.log('ReserveHealthService: 沒有預約日期，無法檢查');
  //     return;
  //   }

  //   // 計算距離健檢的天數
  //   const today = new Date();
  //   today.setHours(0, 0, 0, 0);

  //   const bookDate = this.parseBookDate(myReservation.bookDate);
  //   bookDate.setHours(0, 0, 0, 0);

  //   const daysUntil = Math.ceil((bookDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  //   const isBookDatePassed = today > bookDate;

  //   console.log('ReserveHealthService: API 檢查前的日期判斷:', {
  //     today: today.toISOString(),
  //     bookDate: bookDate.toISOString(),
  //     daysUntil,
  //     isBookDatePassed
  //   });

  //   // 如果健檢日已過或距離健檢超過7天，不需要檢查問卷內容
  //   if (isBookDatePassed) {
  //     console.log('ReserveHealthService: 健檢日已過，設定為 OVERDUE');
  //     this.updateQuestionnaireStatus('OVERDUE');
  //     return;
  //   }

  //   if (daysUntil > 7) {
  //     console.log('ReserveHealthService: 距離健檢超過7天，維持 WAITING 狀態');
  //     this.updateQuestionnaireStatus('WAITING');
  //     return;
  //   }

  //   // 只有在距離健檢 ≤ 7天時，才檢查問卷內容
  //   console.log('ReserveHealthService: 距離健檢 ≤ 7天，檢查問卷內容');

  //   // 從 SharedStorage 取得使用者資訊
  //   const idLoginRequest = this.sharedStorageService.get('idLoginRequest');
  //   const createMemberRequest = this.sharedStorageService.get('createMemberRequest');

  //   const idNo = idLoginRequest?.idNo ?? createMemberRequest?.idNo ?? '';
  //   const name = idLoginRequest?.name ?? createMemberRequest?.name ?? '';

  //   if (!idNo) {
  //     console.log('ReserveHealthService: 無法取得 idNo，無法調用 API');
  //     return;
  //   }

  //   // 使用正確的 UserPlanRequest 格式
  //   const userPlanRequest: UserPlanRequest = {
  //     orgNo: '',
  //     gender: '',
  //     birthday: '',
  //     age: '0',
  //     idNo,
  //     name,
  //     cell: '',
  //     email: '',
  //     telH: '',
  //     telO: '',
  //     zipCode: '',
  //     history: [],
  //     family: [],
  //     subjectiveSymptoms: [],
  //     identityType: '0',  // 身分證字號（歷年體況）
  //     funcType: '1',      // 會員中心
  //   };

  //   console.log('ReserveHealthService: CB-043 API 參數:', userPlanRequest);

  //   // 使用 getQuestionnaireField 方法
  //   this.getQuestionnaireField(userPlanRequest)
  //     .subscribe({
  //       next: (res) => {
  //         console.log('ReserveHealthService: CB-043 API 回應:', res);

  //         if (res.statusCode === 'S0000' && res.data?.questions) {
  //           const questions = res.data.questions;
  //           const questionCount = questions.length;
  //           console.log('ReserveHealthService: CB-043 問題數量:', questionCount);

  //           // 使用完整性檢查邏輯，而不是單純依賴問題數量
  //           const questionnaireData = {
  //             caseNo,
  //             questions: questions,
  //             savedAt: new Date().toISOString()
  //           };

  //           // 檢查問卷是否真正完整
  //           const isComplete = this.checkIfQuestionnaireIsComplete(questionnaireData);

  //           if (isComplete) {
  //             console.log('ReserveHealthService: 發現完整問卷，設定為 COMPLETED');
  //             this.updateQuestionnaireStatus('COMPLETED');

  //             // 將資料儲存到 SharedStorage
  //             this.sharedStorageService.set('savedQuestionnaireData', questionnaireData as any);
  //           } else {
  //             console.log('ReserveHealthService: 問卷不完整，設定為 AVAILABLE');
  //             this.updateQuestionnaireStatus('AVAILABLE');
  //           }
  //         } else {
  //           console.log('ReserveHealthService: CB-043 沒有資料，設定為 AVAILABLE');
  //           this.updateQuestionnaireStatus('AVAILABLE');
  //         }
  //       },
  //       error: (error) => {
  //         console.error('ReserveHealthService: CB-043 API 調用失敗:', error);
  //         // API 失敗時，使用原本的邏輯
  //         this.checkAndUpdateQuestionnaireStatus();
  //       }
  //     });
  // }

  /**
   * 將 bookDate 字串轉換為 Date 物件
   */
  private parseBookDate(bookDate: string): Date {
    const year = parseInt(bookDate.substring(0, 4));
    const month = parseInt(bookDate.substring(4, 6)) - 1;
    const day = parseInt(bookDate.substring(6, 8));
    return new Date(year, month, day);
  }

  /**
   * 取得個人、家族、自覺症狀健調表表格歷史資料
   */
  getQuestionnaireField(
    userPlanRequest: UserPlanRequest
  ): Observable<ApiResponse<QuestionnaireResponse>> {
    const payload = {
      caseNo: userPlanRequest.caseNo,
      idNo: userPlanRequest.idNo,
      questionnaireTypeId: userPlanRequest.questionnaireTypeId
    };

    return this.http.post<ApiResponse<QuestionnaireResponse>>(
      API_ENDPOINTS.QUESTIONNAIRE_RESPONSES,
      payload
    );
  }

  /**
   * 取得預約資訊 (CB-007)
   */
  // getAppointmentInfo(): Observable<ApiResponse<AppointmentInfoResponse>> {
  //   return this.http.get<ApiResponse<AppointmentInfoResponse>>(
  //     API_ENDPOINTS.HEALTH_CHECK_PLAN
  //   );
  // }

  /**
   * 更新問卷狀態
   * @param status 新的問卷狀態
   */
  updateQuestionnaireStatus(status: QuestionnaireStatus): void {
    const currentStatus = this.questionnaireStatusSubject.value;

    if (currentStatus !== status) {
      console.log('ReserveHealthService: 更新問卷狀態從', currentStatus, '到', status);
      this.questionnaireStatusSubject.next(status);
    } else {
      console.log('ReserveHealthService: 狀態無變化，維持', status);
    }
  }

  /**
   * 重新計算並更新問卷狀態
   * 當 SharedStorage 資料變化時呼叫
   */
  recalculateQuestionnaireStatus(): void {
    console.log('ReserveHealthService: 重新計算問卷狀態');
    const newStatus = this.getInitialQuestionnaireStatus();

    if (newStatus !== this.questionnaireStatusSubject.value) {
      console.log('ReserveHealthService: 重新計算後狀態變化，從', this.questionnaireStatusSubject.value, '到', newStatus);
      this.questionnaireStatusSubject.next(newStatus);
    } else {
      console.log('ReserveHealthService: 重新計算後狀態無變化，維持', newStatus);
    }
  }

  /**
   * 獲取當前問卷狀態
   * @returns 當前的問卷狀態
   */
  getCurrentQuestionnaireStatus(): QuestionnaireStatus {
    return this.questionnaireStatusSubject.value;
  }

  /**
   * 檢查並更新問卷狀態（由 member component 觸發）
   */
  checkAndUpdateQuestionnaireStatus(): void {
    console.log('ReserveHealthService: 開始檢查並更新問卷狀態');

    try {
      // 從 SharedStorage 取得預約資訊
      const myReservation: any = this.sharedStorageService.get('myReservation');

      if (!myReservation?.bookDate || !myReservation?.bookStatus) {
        console.log('ReserveHealthService: 沒有預約資訊，設定為 NOT_NEEDED');
        this.updateQuestionnaireStatus('NOT_NEEDED');
        return;
      }

      // 檢查預約狀態是否為正常(1)
      if (myReservation.bookStatus !== '1') {
        console.log('ReserveHealthService: 預約狀態非正常，設定為 NOT_NEEDED');
        this.updateQuestionnaireStatus('NOT_NEEDED');
        return;
      }

      // 計算距離健檢的天數
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const bookDate = this.parseBookDate(myReservation.bookDate);
      bookDate.setHours(0, 0, 0, 0);

      const daysUntil = Math.ceil((bookDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      const isBookDatePassed = today > bookDate;

      console.log('ReserveHealthService: 健檢日期判斷:', {
        today: today.toISOString(),
        bookDate: bookDate.toISOString(),
        daysUntil,
        isBookDatePassed
      });

      // 健檢日已過 → 逾期
      if (isBookDatePassed) {
        console.log('ReserveHealthService: 健檢日已過，設定為 OVERDUE');
        this.updateQuestionnaireStatus('OVERDUE');
        return;
      }

      // 距離健檢 > 7天 → 等待開放
      if (daysUntil > 7) {
        console.log('ReserveHealthService: 距離健檢超過7天，設定為 WAITING');
        this.updateQuestionnaireStatus('WAITING');
        return;
      }

      // 距離健檢 ≤ 7天，檢查是否已完成問卷
      const hasCompleteData = this.checkIfQuestionnaireDataExistsInStorage();

      if (hasCompleteData) {
        console.log('ReserveHealthService: 發現完整問卷，設定為 COMPLETED');
        this.updateQuestionnaireStatus('COMPLETED');
      } else {
        console.log('ReserveHealthService: 問卷未完成，設定為 AVAILABLE');
        this.updateQuestionnaireStatus('AVAILABLE');
      }

    } catch (error) {
      console.error('ReserveHealthService: 檢查並更新問卷狀態失敗:', error);
    }
  }



  /**
   * 檢查 SharedStorage 中是否有**完整儲存過**的問卷資料
   * 不是只檢查有沒有資料，而是檢查是否真的儲存過（點過儲存按鈕）
   */
  private checkIfQuestionnaireDataExistsInStorage(): boolean {
    try {
      const savedData = this.sharedStorageService.get('savedQuestionnaireData');

      // 沒有資料 → 未完成
      if (!savedData?.questions) {
        return false;
      }

      // 檢查是否有 savedAt 時間戳記
      // 只有點過「儲存」按鈕才會有這個時間戳記
      if (!savedData.savedAt) {
        return false;
      }

      // 檢查是否為同一個案件
      const myReservation = this.sharedStorageService.get('myReservation');
      if (savedData.caseNo !== myReservation?.caseNo) {
        return false;
      }

      // 檢查資料是否過期（超過 7 天）
      const savedTime = new Date(savedData.savedAt);
      const now = new Date();
      const daysDiff = (now.getTime() - savedTime.getTime()) / (1000 * 60 * 60 * 24);
      
      if (daysDiff > 7) {
        // 資料過期，清除並視為未完成
        this.sharedStorageService.remove('savedQuestionnaireData');
        return false;
      }

      // 檢查問卷是否真正完整（8頁都填寫完成）
      const isComplete = this.checkIfQuestionnaireIsComplete(savedData);

      console.log('ReserveHealthService: 問卷完整性檢查結果:', isComplete);

      return isComplete;
    } catch (error) {
      console.error('Error checking questionnaire data:', error);
      return false;
    }
  }

  /**
   * 檢查問卷是否真正完整（基於問題數量和關鍵頁面）
   * @param savedData SharedStorage 中的問卷資料
   * @returns 是否完整
   */
  private checkIfQuestionnaireIsComplete(savedData: any): boolean {
    if (!savedData?.questions || !Array.isArray(savedData.questions)) {
      return false;
    }

    const questions = savedData.questions;
    const questionCount = questions.length;

    // 檢查所有8頁是否都有填寫（除了作業經歷為可選）
    const hasPersonalHistory = questions.some((q: any) => q.questionId === '474');      // 步驟2 - 個人病史

    // 家族病史：檢查 questionId 483 或 484（其他家族病史）
    const hasFamilyHistory = questions.some((q: any) => q.questionId === '483') ||
                            questions.some((q: any) => q.questionId === '484');

    // 自覺症狀：檢查 questionId 472 或 473（其他自覺症狀）
    const hasSubjectiveSymptoms = questions.some((q: any) => q.questionId === '472') ||
                                 questions.some((q: any) => q.questionId === '473');

    const hasLongTermMedication = questions.some((q: any) => q.questionId === '481');   // 步驟5 - 長期藥物
    const hasLifestyle = questions.some((q: any) => q.questionId === '485');            // 步驟6 - 生活習慣

    // 檢查基本資料頁面（步驟8 - 其他資訊）
    const hasBasicInfo = questions.some((q: any) =>
      ['511', '513', '514', '515'].includes(q.questionId)
    );

    // 檢查作業經歷頁面（步驟7 - 作業經歷，可選）
    const hasWorkExperience = questions.some((q: any) =>
      ['519', '523'].includes(q.questionId)
    );

    // 完整問卷的判斷條件（優化版）：
    // 1. 包含所有必填頁面：個人病史、家族病史、自覺症狀、長期藥物、生活習慣、基本資料
    // 2. 作業經歷為完全可選
    // 3. 不再依賴問題數量，因為每個人填寫的內容不同
    const hasAllRequiredPages = hasPersonalHistory && hasFamilyHistory && hasSubjectiveSymptoms &&
                               hasLongTermMedication && hasLifestyle && hasBasicInfo;

    const isComplete = hasAllRequiredPages;

    console.log('ReserveHealthService: 問卷完整性檢查:', {
      questionCount,
      hasPersonalHistory,
      hasFamilyHistory,
      hasSubjectiveSymptoms,
      hasLongTermMedication,
      hasLifestyle,
      hasBasicInfo,
      hasWorkExperience,
      hasAllRequiredPages,
      isComplete
    });

    return isComplete;
  }

  /**
   * 檢查是否應該顯示未完成標籤
   * 條件:
   * 1. 狀態是 AVAILABLE
   * 2. 且問卷資料不存在(尚未儲存過)
   * @returns true 如果應該顯示未完成標籤
   */
  shouldShowUnfinishedTag(): boolean {
    const status = this.getCurrentQuestionnaireStatus();

    console.log('ReserveHealthService: shouldShowUnfinishedTag 檢查', {
      currentStatus: status
    });

    // 只有在 AVAILABLE 狀態下才需要進一步檢查
    if (status !== 'AVAILABLE') {
      console.log('ReserveHealthService: 狀態不是 AVAILABLE，不顯示未完成 tag');
      return false;
    }

    // 檢查是否已經有儲存過的資料
    const hasData = this.checkIfQuestionnaireDataExistsInStorage();
    const shouldShow = !hasData;

    console.log('ReserveHealthService: AVAILABLE 狀態檢查結果', {
      hasData,
      shouldShowTag: shouldShow
    });

    // AVAILABLE 且沒有資料 → 顯示「未完成」
    return shouldShow;
  }

  /**
   * 重新載入問卷狀態
   */
  // refreshQuestionnaireStatus(): void {
  //   this.getAppointmentInfo().subscribe(response => {
  //     if (response.success && response.data) {
  //       const status = this.calculateQuestionnaireStatus(response.data);
  //       this.questionnaireStatusSubject.next(status);
  //     }
  //   });
  // }

  /**
   * 根據預約資訊計算問卷狀態
   */
  // private calculateQuestionnaireStatus(
  //   appointmentInfo: AppointmentInfoResponse
  // ): QuestionnaireStatus {
  //   const { bookStatus, bookDate } = appointmentInfo;

  //   // 如果沒有預約,則不需要填寫問卷
  //   if (!bookStatus || !bookDate) {
  //     return 'NOT_NEEDED';
  //   }

  //   // 已報到或之後的狀態,不需要填寫問卷
  //   if (
  //     bookStatus === BOOK_STATUS.CHECKED_IN ||
  //     bookStatus === BOOK_STATUS.REPORT_PRINTED ||
  //     bookStatus === BOOK_STATUS.REPORT_SENT
  //   ) {
  //     return 'NOT_NEEDED';
  //   }

  //   // 檢查健檢日期是否已過
  //   const today = new Date();
  //   const checkDate = new Date(
  //     parseInt(bookDate.substring(0, 4)),
  //     parseInt(bookDate.substring(4, 6)) - 1,
  //     parseInt(bookDate.substring(6, 8))
  //   );

  //   const isOverdue = checkDate < today;

  //   // 健檢日已過但未報到
  //   if (isOverdue && bookStatus === BOOK_STATUS.NORMAL) {
  //     return 'OVERDUE';
  //   }

  //   // 正常狀態,需要填寫問卷
  //   if (bookStatus === BOOK_STATUS.NORMAL) {
  //     return 'AVAILABLE';
  //   }

  //   return 'WAITING';
  // }

  /**
   * CB-022 儲存健調表
   */
  saveQuestionnaire(request: SaveQuestionnaireRequest): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      API_ENDPOINTS.SAVE_QUESTIONNAIRE,
      request
    );
  }

  /**
  * 從 Storage 取得問卷資料（API 或已儲存的）
  */
  // getStoredQuestionnaireData(): QuestionnaireResponse | null {
  //   try {
  //     // 優先使用已儲存的資料
  //     const savedData = this.sharedStorageService.get('savedQuestionnaireData') as any;
  //     if (savedData?.questions) {
  //       return {
  //         questionnaireId: savedData.questionnaireId || '20',
  //         questions: savedData.questions
  //       };
  //     }
      
  //     // 其次使用 API 載入的資料
  //     const apiData = this.sharedStorageService.get('questionnaireApiData') as any;
  //     if (apiData?.questions) {
  //       return {
  //         questionnaireId: apiData.questionnaireId || '20',
  //         questions: apiData.questions
  //       };
  //     }
      
  //     return null;
  //   } catch (error) {
  //     console.error('Error getting stored questionnaire data:', error);
  //     return null;
  //   }
  // }

  /**
   * 取得特定選項的答案內容
   */
  // getOptionAnswer(questionId: string, choiceId: string): string {
  //   try {
  //     const data = this.getStoredQuestionnaireData();
      
  //     if (data?.questions) {
  //       const question = data.questions.find((q) => q.questionId === questionId);
  //       if (question) {
  //         const answer = question.questionAnswers.find((a) => a.choiceId === choiceId);
  //         return answer?.answer || '';
  //       }
  //     }
      
  //     return '';
  //   } catch (error) {
  //     console.error('Error getting option answer:', error);
  //     return '';
  //   }
  // }
}